import React from 'react';
import {
  Alert, Box, Button, Chip, IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TableSortLabel, Tooltip, Typography
} from '@mui/material';
import { HistoryRounded as HistoryIcon } from '@mui/icons-material';
import { fmtMoney } from '../../Documents/format';
import { BUCKET_COLORS, ColumnId, PAGE_SIZES, TableColumn, bucketOf } from '../constants';
import { daysLeftLabel, fmtDay, fmtInt, fmtQty, fmtStamp } from '../format';
import { JournalEntry, JournalPagination, JournalTotals, SortField, SortState } from '../types';
import ColumnsMenu from './ColumnsMenu';

interface Props {
  rows: JournalEntry[];
  pagination: JournalPagination;
  totals: JournalTotals | null;
  loading: boolean;
  error: string | null;
  sort: SortState;
  columns: TableColumn[];
  visible: ColumnId[];
  columnsDefault: boolean;
  hasFilters: boolean;
  onToggleColumn: (id: ColumnId) => void;
  onResetColumns: () => void;
  onSort: (key: SortField) => void;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
  onOpen: (entry: JournalEntry) => void;
  onResetFilters: () => void;
  onRetry: () => void;
}

const BORDER = '#e2e8f0';
const ACTIONS_WIDTH = 48;

/** Цвета основных категорий журнала; прочие - нейтральный серый */
const CATEGORY_RGB: Record<string, string> = {
  'Медикаменты New': '37,99,235',
  'БАД New': '13,148,136',
  'Сангигиена NEW': '217,119,6'
};
const NEUTRAL_RGB = '100,116,139';

/** Отметка аптеки о расхождении серии - выделяем, чтобы не терялась среди обычных комментариев */
const SERIAL_MISMATCH = /серия не совпадает/i;

const CategoryChip: React.FC<{ name: string }> = ({ name }) => {
  if (!name) return <Typography component="span" sx={{ fontSize: 12.5, color: '#94a3b8' }}>-</Typography>;
  const rgb = CATEGORY_RGB[name] || NEUTRAL_RGB;
  return (
    <Chip
      size="small" label={name} title={name}
      sx={{ height: 22, maxWidth: '100%', fontSize: 11.5, fontWeight: 600, borderRadius: 1.25, color: `rgb(${rgb})`, bgcolor: `rgba(${rgb},0.08)` }}
    />
  );
};

const DaysChip: React.FC<{ entry: JournalEntry }> = ({ entry }) => {
  const c = BUCKET_COLORS[bucketOf(entry.daysLeft)];
  const atRecord = entry.daysAtRecord === null ? null : `на дату записи: ${daysLeftLabel(entry.daysAtRecord)}`;
  return (
    <Tooltip title={atRecord || ''} disableHoverListener={!atRecord}>
      <Chip
        size="small" label={daysLeftLabel(entry.daysLeft)}
        sx={{ height: 22, fontSize: 11.5, fontWeight: 600, borderRadius: 1.25, color: c.color, bgcolor: c.bg }}
      />
    </Tooltip>
  );
};

function renderCell(id: ColumnId, row: JournalEntry, visible: ColumnId[]): React.ReactNode {
  switch (id) {
    case 'pharmacy':
      return <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>№{row.pharmacyNum}</Typography>;
    case 'region':
      return row.cityId || '-';
    case 'name': {
      const ids = [
        visible.indexOf('code') === -1 && row.nomenclatureCode ? `код ${row.nomenclatureCode}` : '',
        visible.indexOf('barcode') === -1 && row.barcode ? `ШК ${row.barcode}` : ''
      ].filter(Boolean).join(' · ');
      return (
        <Box sx={{ minWidth: 0 }}>
          <Typography
            title={row.materialName}
            sx={{ fontSize: 13, fontWeight: 500, color: '#0f172a', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {row.materialName || '-'}
          </Typography>
          {ids && <Typography sx={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }} noWrap>{ids}</Typography>}
        </Box>
      );
    }
    case 'producer':
      return <Typography sx={{ fontSize: 12.5, color: '#475569' }} noWrap title={row.producer}>{row.producer || '-'}</Typography>;
    case 'expiry': {
      if (!row.expiryDate) return <Typography sx={{ fontSize: 12.5, color: '#94a3b8' }}>не указан</Typography>;
      const c = BUCKET_COLORS[bucketOf(row.daysLeft)];
      return <Typography sx={{ fontSize: 13, fontWeight: 600, color: c.color, whiteSpace: 'nowrap' }}>{fmtDay(row.expiryDate)}</Typography>;
    }
    case 'daysLeft':
      return <DaysChip entry={row} />;
    case 'serial':
      return <Typography sx={{ fontSize: 12.5, fontFamily: 'monospace', wordBreak: 'break-all' }}>{row.serialNumber || '-'}</Typography>;
    case 'qty':
      return fmtQty(row.quantity);
    case 'qtyPcs':
      return fmtQty(row.quantityPcs);
    case 'price':
      return fmtMoney(row.price, 2);
    case 'totalSum':
      return <Typography sx={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtMoney(row.totalSum, 2)}</Typography>;
    case 'manager':
      return <Typography sx={{ fontSize: 12.5, color: '#475569' }} noWrap title={row.managerName}>{row.managerName || '-'}</Typography>;
    case 'category':
      return <CategoryChip name={row.categoryName} />;
    case 'priceGroup':
      return row.priceGroup || '-';
    case 'rxotc':
      return row.rxotc || '-';
    case 'code':
      return <Typography sx={{ fontSize: 12.5, fontFamily: 'monospace' }}>{row.nomenclatureCode || '-'}</Typography>;
    case 'barcode':
      return <Typography sx={{ fontSize: 12.5, fontFamily: 'monospace', wordBreak: 'break-all' }}>{row.barcode || '-'}</Typography>;
    case 'comment': {
      if (!row.comment) return <Typography sx={{ fontSize: 12.5, color: '#cbd5e1' }}>-</Typography>;
      const mismatch = SERIAL_MISMATCH.test(row.comment);
      return (
        <Typography
          title={row.comment}
          sx={{
            fontSize: 12.5, color: mismatch ? '#b45309' : '#475569', fontWeight: mismatch ? 600 : 400,
            bgcolor: mismatch ? 'rgba(245,158,11,0.1)' : undefined, px: mismatch ? 0.75 : 0, py: mismatch ? 0.25 : 0, borderRadius: 1,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
          }}
        >
          {row.comment}
        </Typography>
      );
    }
    case 'recordDate':
      return <Typography sx={{ fontSize: 12.5, color: '#475569', whiteSpace: 'nowrap' }}>{fmtStamp(row.recordDate)}</Typography>;
    case 'updateDate': {
      const changed = Boolean(row.updateDate && row.recordDate && row.updateDate > row.recordDate);
      return (
        <Box>
          <Typography sx={{ fontSize: 12.5, color: changed ? '#0f172a' : '#94a3b8', whiteSpace: 'nowrap' }}>{fmtStamp(row.updateDate)}</Typography>
          {row.updates ? <Typography sx={{ fontSize: 11, color: '#0284c7' }}>изменений: {row.updates}</Typography> : null}
        </Box>
      );
    }
    case 'createdBy':
      return <Typography sx={{ fontSize: 12.5, color: '#475569' }} noWrap title={row.createdBy}>{row.createdBy || '-'}</Typography>;
    default:
      return '-';
  }
}

const JournalTable: React.FC<Props> = ({
  rows, pagination, totals, loading, error, sort, columns, visible, columnsDefault, hasFilters,
  onToggleColumn, onResetColumns, onSort, onPage, onLimit, onOpen, onResetFilters, onRetry
}) => {
  const showError = Boolean(error) && !rows.length;
  const showEmpty = !loading && !error && !rows.length;
  const minWidth = columns.reduce((acc, c) => acc + c.width, ACTIONS_WIDTH);
  const colSpan = columns.length + 1;

  return (
    <Paper variant="outlined" sx={{ borderColor: BORDER, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
      <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3 }} />}</Box>
      <Box sx={{ px: 2, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>
          {totals ? (
            <>
              Найдено <Box component="b" sx={{ color: '#0f172a' }}>{fmtInt(totals.count)}</Box> зап.
              {' · '}{fmtQty(totals.qty)} уп.
              {' · '}<Box component="b" sx={{ color: '#0f172a' }}>{fmtMoney(totals.sum, 2)}</Box>
            </>
          ) : 'Загрузка записей…'}
        </Typography>
        <ColumnsMenu visible={visible} isDefault={columnsDefault} onToggle={onToggleColumn} onReset={onResetColumns} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 1.5 }} action={<Button color="inherit" size="small" onClick={onRetry}>Повторить</Button>}>
          {error}
        </Alert>
      )}

      <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)', minHeight: 260 }}>
        <Table size="small" stickyHeader sx={{ minWidth, tableLayout: 'fixed', opacity: loading && rows.length ? 0.6 : 1, transition: 'opacity .15s' }}>
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell
                  key={col.id} align={col.align} sortDirection={col.sort && sort.sortBy === col.sort ? sort.sortOrder : false}
                  sx={{ width: col.width, fontWeight: 700, fontSize: 12, color: '#475569', bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap' }}
                >
                  {col.sort ? (
                    <TableSortLabel
                      active={sort.sortBy === col.sort} direction={sort.sortBy === col.sort ? sort.sortOrder : 'asc'}
                      onClick={() => onSort(col.sort as SortField)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : col.label}
                </TableCell>
              ))}
              <TableCell sx={{ width: ACTIONS_WIDTH, bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}` }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow
                key={row.id} hover onClick={() => onOpen(row)}
                sx={{ cursor: 'pointer', '& td': { borderBottom: '1px solid #f1f5f9', py: 0.75, fontSize: 13, verticalAlign: 'middle' } }}
              >
                {columns.map(col => (
                  <TableCell key={col.id} align={col.align} sx={{ overflow: 'hidden' }}>
                    {renderCell(col.id, row, visible)}
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ px: 0.5 }}>
                  <Tooltip title={`История записи #${row.id}`}>
                    <IconButton
                      size="small" aria-label="История записи"
                      onClick={e => { e.stopPropagation(); onOpen(row); }}
                      sx={{ color: row.updates ? '#0284c7' : '#94a3b8' }}
                    >
                      <HistoryIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {showError && (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 6, color: '#64748b' }}>
                  <Typography variant="body2">Данные не загружены</Typography>
                </TableCell>
              </TableRow>
            )}

            {showEmpty && (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 6, color: '#64748b' }}>
                  <Typography variant="body2">{hasFilters ? 'По заданным фильтрам записей не найдено' : 'За выбранный период записей нет'}</Typography>
                  {hasFilters && (
                    <Typography
                      variant="body2" onClick={onResetFilters}
                      sx={{ mt: 0.5, color: 'primary.main', cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
                    >
                      Сбросить фильтры
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div" count={pagination.total} page={Math.max(0, pagination.page - 1)} rowsPerPage={pagination.limit}
        rowsPerPageOptions={PAGE_SIZES}
        onPageChange={(_, p) => onPage(p + 1)}
        onRowsPerPageChange={e => onLimit(Number(e.target.value))}
        labelRowsPerPage="На странице:"
        labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString('ru-RU')}–${to.toLocaleString('ru-RU')} из ${count.toLocaleString('ru-RU')}`}
        sx={{ borderTop: `1px solid ${BORDER}` }}
      />
    </Paper>
  );
};

export default React.memo(JournalTable);
